import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Skin } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Lightning } from '@sorare/core/src/atoms/icons/Lightning';
import { CenterVertically } from '@sorare/core/src/atoms/layout/CenterVertically';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  HeadlineL,
  LabelM,
  LabelS,
  LabelXS,
} from '@sorare/core/src/atoms/typography';
import { SplitProgressBar } from '@sorare/core/src/atoms/ui/SplitProgressBar';
import { ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { CollectionHexagon } from '#src/components/lineup/LineupBonusDetails/CollectionHexagon/index.tsx';
import { SpecialEditionHexagon } from '#src/components/lineup/LineupBonusDetails/SpecialEditionHexagon/index.tsx';

import { BreakdownBlock } from '../AppearanceBonusBreakdown/BreakdownBlock/index.tsx';
import { CardBlock } from '../AppearanceBonusBreakdown/CardBlock/index.tsx';
import { messages } from '../AppearanceBonusBreakdown/messages.ts';
import type { SetAppearanceBonusBreakdown_appearance } from './__generated__/index.graphql.ts';

const Content = styled(Vertical)`
  padding: var(--double-unit) 0;
`;
const BreakdownWrapper = styled(Vertical).attrs({ gap: 3 })`
  position: relative;
  padding-top: var(--triple-unit);
  isolation: isolate;
`;
const LinesSvg = styled.svg`
  width: 100%;
  position: absolute;
  top: 0;
  z-index: -1;
`;
const Blocks = styled.div`
  display: flex;
  min-width: calc(33 * var(--unit));

  > * {
    flex: 1;
  }

  gap: var(--quadruple-unit);
  align-items: start;
`;
const BrandHeaderlineL = styled(HeadlineL)`
  background: var(--c-black);
  align-self: center;
  padding: var(--half-unit) var(--unit);

  font-family: var(--brand-font);
  text-shadow: 0px 0px 8px rgba(255, 255, 255, 0.58);
`;
const BrandLabelS = styled(LabelS)`
  font-family: var(--brand-font);
`;
const BrandLabelXS = styled(LabelXS)`
  font-family: var(--brand-font);
`;
const SpecialEditionTag = styled(Horizontal).attrs({ center: true })`
  background: var(--c-nd-400);
  padding: 0 var(--unit);
  height: var(--double-and-a-half-unit);
  border-radius: 10px;
  box-shadow: 0 0 20px 0 #df88ff;

  &.shiny {
    background: var(--c-gradient-shiny);
  }
  &.holo {
    background: var(--c-gradient-holo);
  }
  &.full_art {
    background: var(--c-gradient-legendary);
  }
`;

type Props = {
  appearance: SetAppearanceBonusBreakdown_appearance;
};

export const SetAppearanceBonusBreakdown = ({ appearance }: Props) => {
  const { generateSportContextPath } = useSportContext();

  const { powerBreakdownOrPowerPointsBreakdown } = appearance;
  if (!powerBreakdownOrPowerPointsBreakdown) {
    return null;
  }

  const powerBreakdown = isType(
    powerBreakdownOrPowerPointsBreakdown,
    'PowerBreakdown'
  )
    ? powerBreakdownOrPowerPointsBreakdown
    : null;
  const powerPointsBreakdown = isType(
    powerBreakdownOrPowerPointsBreakdown,
    'PowerPointsBreakdown'
  )
    ? powerBreakdownOrPowerPointsBreakdown
    : null;
  const { collectionBasisPoints, specialEditionCardsBasisPoints } =
    powerBreakdown || {};
  const { collectionPoints, specialEditionCardsPoints } =
    powerPointsBreakdown || {};

  const cardCollectionCard =
    appearance?.anyCard && isType(appearance.anyCard, 'Card')
      ? appearance.anyCard.cardCollectionCards.at(0)
      : null;

  const { cardCollection, userCardCollection } = cardCollectionCard || {};

  const reachedMaxCollectionBonus = cardCollection?.bonusThresholds.every(
    ({ threshold }) => (userCardCollection?.score || 0) > threshold
  );

  const hasSpecialEditionBonus =
    !!specialEditionCardsBasisPoints || !!specialEditionCardsPoints;

  const { skin = Skin.STANDARD, skinName } =
    appearance.anyCard?.myCardCollectionSlot?.skinEdition || {};
  const totalBasisPoints =
    (collectionBasisPoints || 0) + (specialEditionCardsBasisPoints || 0);
  const totalPoints =
    (collectionPoints || 0) + (specialEditionCardsPoints || 0);

  return (
    <CenterVertically>
      <Content center>
        <CardBlock appearance={appearance} />
        <BreakdownWrapper>
          {hasSpecialEditionBonus ? (
            <LinesSvg
              xmlns="http://www.w3.org/2000/svg"
              width="150"
              height="75"
              viewBox="0 0 150 75"
              fill="none"
            >
              <path d="M75 0V37" stroke="white" strokeOpacity="0.17" />
              <path d="M1 75V40H73" stroke="white" strokeOpacity="0.17" />
              <path d="M149 75V40H77" stroke="white" strokeOpacity="0.17" />
            </LinesSvg>
          ) : (
            <LinesSvg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="75"
              viewBox="0 0 11 75"
              fill="none"
            >
              <path d="M6 0 V75" stroke="white" strokeOpacity="0.17" />
            </LinesSvg>
          )}

          <BrandHeaderlineL>
            <Horizontal center gap={0.5}>
              {isType(
                powerBreakdownOrPowerPointsBreakdown,
                'PowerBreakdown'
              ) ? (
                <FormattedMessage
                  {...messages.numberPercent}
                  values={{
                    number: (
                      <FormattedNumber
                        value={totalBasisPoints / 100}
                        maximumFractionDigits={1}
                        signDisplay="always"
                      />
                    ),
                    span: chunks => (
                      <BrandLabelS bold color="var(--c-nd-600)">
                        {chunks}
                      </BrandLabelS>
                    ),
                  }}
                />
              ) : (
                <FormattedMessage
                  {...fantasy.ordinalPoints}
                  values={{
                    score: (
                      <FormattedNumber
                        value={totalPoints}
                        maximumFractionDigits={1}
                        signDisplay="always"
                      />
                    ),
                    sup: chunks => (
                      <BrandLabelXS bold uppercase color="var(--c-nd-600)">
                        {chunks}
                      </BrandLabelXS>
                    ),
                  }}
                />
              )}
            </Horizontal>
          </BrandHeaderlineL>
          <Blocks>
            <BreakdownBlock
              border={!!reachedMaxCollectionBonus}
              percentBasisPoints={
                isType(powerBreakdownOrPowerPointsBreakdown, 'PowerBreakdown')
                  ? powerBreakdownOrPowerPointsBreakdown.collectionBasisPoints
                  : null
              }
              points={
                isType(
                  powerBreakdownOrPowerPointsBreakdown,
                  'PowerPointsBreakdown'
                )
                  ? powerBreakdownOrPowerPointsBreakdown.collectionPoints
                  : null
              }
              Icon={CollectionHexagon}
              label={
                <Horizontal gap={0.5}>
                  <Lightning color="var(--c-yellow-300)" />
                  <LabelS bold color="var(--c-yellow-300)">
                    <FormattedMessage defaultMessage="Collection" />
                  </LabelS>
                </Horizontal>
              }
              bar={
                <SplitProgressBar
                  thresholds={
                    cardCollection?.bonusThresholds.map(({ threshold }) => ({
                      score: threshold,
                    })) || []
                  }
                  currentScore={userCardCollection?.score || 0}
                  color="var(--c-yellow-400)"
                />
              }
              cta={
                !!cardCollection &&
                isType(
                  powerBreakdownOrPowerPointsBreakdown,
                  'PowerBreakdown'
                ) && (
                  <Button
                    fullWidth
                    size="compact"
                    color="tertiary"
                    startIcon={<Lightning color="var(--c-yellow-400)" />}
                    to={
                      !reachedMaxCollectionBonus
                        ? generateSportContextPath(
                            ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG,
                            {
                              params: { collectionSlug: cardCollection.slug },
                            }
                          )
                        : undefined
                    }
                    disabled={!!reachedMaxCollectionBonus}
                  >
                    {reachedMaxCollectionBonus ? (
                      <FormattedMessage {...glossary.max} />
                    ) : (
                      <FormattedMessage defaultMessage="View" />
                    )}
                  </Button>
                )
              }
            />
            {hasSpecialEditionBonus ? (
              <BreakdownBlock
                lightBackground
                border={hasSpecialEditionBonus}
                percentBasisPoints={
                  isType(powerBreakdownOrPowerPointsBreakdown, 'PowerBreakdown')
                    ? powerBreakdownOrPowerPointsBreakdown.specialEditionCardsBasisPoints
                    : null
                }
                points={
                  isType(
                    powerBreakdownOrPowerPointsBreakdown,
                    'PowerPointsBreakdown'
                  )
                    ? powerBreakdownOrPowerPointsBreakdown.specialEditionCardsPoints
                    : null
                }
                Icon={({ children }) => (
                  <SpecialEditionHexagon skin={skin}>
                    {children}
                  </SpecialEditionHexagon>
                )}
                label={
                  <Horizontal gap={0.5}>
                    <LabelS bold color="var(--c-nd-400)">
                      <FormattedMessage defaultMessage="Special edition" />
                    </LabelS>
                  </Horizontal>
                }
                bar={
                  !!skinName && (
                    <SpecialEditionTag className={skin.toLowerCase()}>
                      <LabelM bold color="var(--c-black)">
                        {skinName}
                      </LabelM>
                    </SpecialEditionTag>
                  )
                }
              />
            ) : null}
          </Blocks>
        </BreakdownWrapper>
      </Content>
    </CenterVertically>
  );
};

SetAppearanceBonusBreakdown.fragments = {
  appearance: gql`
    fragment SetAppearanceBonusBreakdown_appearance on AppearanceInterface {
      id
      powerBreakdownOrPowerPointsBreakdown {
        ... on PowerBreakdown {
          collectionBasisPoints
          specialEditionCardsBasisPoints
        }
        ... on PowerPointsBreakdown {
          collectionPoints
          specialEditionCardsPoints
        }
      }
      anyCard {
        slug
        rarity: rarityTyped
        grade
        xpNeededForNextGrade
        myCardCollectionSlot {
          slug
          skinEdition {
            skin
            skinName
          }
        }
        cardCollectionCards {
          id
          userCardCollection {
            id
            score
          }
          cardCollection {
            slug
            bonusThresholds {
              bonus
              threshold
            }
          }
        }
      }
      ...CardBlock_appearance
    }
    ${CardBlock.fragments.appearance}
  ` as TypedDocumentNode<SetAppearanceBonusBreakdown_appearance>,
};
